import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Cloud, Check, Phone, Mail, MapPin, Building, Calendar, DollarSign, Users, BarChart, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { usePrefetch, usePrefetchOnIdle } from '@/hooks/usePrefetch';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const SampleLeadPreview = () => {
  const [sampleState, setSampleState] = useState('');
  const [sampleZip, setSampleZip] = useState('');
  const [loading, setLoading] = useState(false);
  const [sampleLeads, setSampleLeads] = useState([]);

  const handlePreviewSample = async () => {
    if (!sampleState) {
      toast.error('Please select a state');
      return;
    }

    if (!sampleZip || sampleZip.length !== 5) {
      toast.error('Please enter a valid 5-digit ZIP code');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/leads/sample`, {
        state: sampleState,
        zip: sampleZip
      });
      setSampleLeads(response.data.leads);
    } catch (error) {
      toast.error('Failed to load sample leads');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-4 mb-6 max-w-3xl mx-auto">
        <div className="flex-1">
          <Select value={sampleState} onValueChange={setSampleState}>
            <SelectTrigger>
              <SelectValue placeholder="Select State" />
            </SelectTrigger>
            <SelectContent>
              {US_STATES.map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1">
          <Input
            placeholder="Enter ZIP Code"
            value={sampleZip}
            onChange={(e) => setSampleZip(e.target.value)}
            maxLength={5}
            required
          />
        </div>
        <Button
          onClick={handlePreviewSample}
          disabled={loading || !sampleState || !sampleZip}
          className="px-8"
        >
          {loading ? 'Loading...' : 'Preview Leads'}
        </Button>
      </div>

      {sampleLeads.length > 0 && (
        <div className="bg-white rounded-xl border-2 border-slate-200 overflow-hidden shadow-lg">
          <div className="overflow-x-auto max-h-[600px] relative">
            <table className="w-full text-xs border-collapse">
              <thead className="sticky top-0 z-10">
                <tr>
                  <th colSpan="8" className="bg-blue-600 text-white px-3 py-2 text-left text-sm font-bold border-r-2 border-blue-700">
                    Contact Information
                  </th>
                  <th colSpan="5" className="bg-blue-700 text-white px-3 py-2 text-left text-sm font-bold border-r-2 border-blue-800">
                    Demographics & Financial
                  </th>
                  <th colSpan="5" className="bg-blue-800 text-white px-3 py-2 text-left text-sm font-bold">
                    Property & Storm Data
                  </th>
                </tr>
                <tr className="bg-blue-500 text-white">
                  <th className="px-3 py-2 text-left font-semibold border-r border-blue-400 whitespace-nowrap">First Name</th>
                  <th className="px-3 py-2 text-left font-semibold border-r border-blue-400 whitespace-nowrap">Last Name</th>
                  <th className="px-3 py-2 text-left font-semibold border-r border-blue-400 min-w-[140px]">Address</th>
                  <th className="px-3 py-2 text-left font-semibold border-r border-blue-400 whitespace-nowrap">City</th>
                  <th className="px-3 py-2 text-left font-semibold border-r border-blue-400">ST</th>
                  <th className="px-3 py-2 text-left font-semibold border-r border-blue-400">ZIP</th>
                  <th className="px-3 py-2 text-left font-semibold border-r border-blue-400 min-w-[120px]">Phone</th>
                  <th className="px-3 py-2 text-left font-semibold border-r-2 border-blue-700">DNC</th>
                  <th className="px-3 py-2 text-left font-semibold border-r border-blue-400">Age</th>
                  <th className="px-3 py-2 text-left font-semibold border-r border-blue-400 whitespace-nowrap">Own/Rent</th>
                  <th className="px-3 py-2 text-left font-semibold border-r border-blue-400 min-w-[100px]">Home Value</th>
                  <th className="px-3 py-2 text-left font-semibold border-r border-blue-400 min-w-[90px]">Income</th>
                  <th className="px-3 py-2 text-left font-semibold border-r-2 border-blue-800 min-w-[140px]">Credit Score</th>
                  <th className="px-3 py-2 text-left font-semibold border-r border-blue-400 whitespace-nowrap">Home Age</th>
                  <th className="px-3 py-2 text-left font-semibold border-r border-blue-400 whitespace-nowrap">Max Wind</th>
                  <th className="px-3 py-2 text-left font-semibold border-r border-blue-400 whitespace-nowrap">Wind Date</th>
                  <th className="px-3 py-2 text-left font-semibold border-r border-blue-400 whitespace-nowrap">Max Hail</th>
                  <th className="px-3 py-2 text-left font-semibold whitespace-nowrap">Hail Date</th>
                </tr>
              </thead>
              <tbody>
                {sampleLeads.map((lead, idx) => (
                  <tr key={idx} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-blue-50'} hover:bg-blue-100 transition-colors`}>
                    <td className="px-3 py-2 text-slate-900 border-r border-slate-200 font-medium">{lead.firstName}</td>
                    <td className="px-3 py-2 text-slate-600 border-r border-slate-200">
                      <div className="flex items-center gap-1">
                        <Lock className="w-3 h-3 text-slate-400 flex-shrink-0" />
                        <span className="truncate">{lead.lastName}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-slate-600 border-r border-slate-200">
                      <div className="flex items-center gap-1">
                        <Lock className="w-3 h-3 text-slate-400 flex-shrink-0" />
                        <span className="truncate">{lead.address}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-slate-900 border-r border-slate-200">{lead.city}</td>
                    <td className="px-3 py-2 text-slate-900 border-r border-slate-200 font-semibold">{lead.state}</td>
                    <td className="px-3 py-2 text-slate-900 border-r border-slate-200 font-medium">{lead.zip}</td>
                    <td className="px-3 py-2 text-slate-600 border-r border-slate-200">
                      <div className="flex items-center gap-1">
                        <Lock className="w-3 h-3 text-slate-400 flex-shrink-0" />
                        <span>{lead.phone}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 border-r-2 border-slate-300 text-center">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-bold ${
                        lead.dnc === 'Yes' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {lead.dnc}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-slate-900 border-r border-slate-200 text-center">{lead.age}</td>
                    <td className="px-3 py-2 text-slate-900 border-r border-slate-200">{lead.ownerRenter}</td>
                    <td className="px-3 py-2 text-slate-900 border-r border-slate-200 whitespace-nowrap font-medium text-green-700">{lead.homeValue}</td>
                    <td className="px-3 py-2 text-slate-900 border-r border-slate-200 whitespace-nowrap font-medium text-green-700">{lead.income}</td>
                    <td className="px-3 py-2 text-slate-900 border-r-2 border-slate-300 font-medium text-blue-700">{lead.credit}</td>
                    <td className="px-3 py-2 text-slate-900 border-r border-slate-200 text-center">{lead.homeAge}</td>
                    <td className="px-3 py-2 text-slate-900 border-r border-slate-200 text-center">{lead.maxWind}</td>
                    <td className="px-3 py-2 text-slate-900 border-r border-slate-200 text-center">{lead.windDate}</td>
                    <td className="px-3 py-2 text-slate-900 border-r border-slate-200 text-center">{lead.maxHail}</td>
                    <td className="px-3 py-2 text-slate-900 text-center">{lead.hailDate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="bg-amber-50 border-t-2 border-amber-300 p-5 space-y-3">
            <p className="text-sm text-amber-900">
              <strong>📊 Data Availability Notice:</strong> Not all data is available for every lead (e.g., email, cell phone, land line, income range). 
              Data availability varies by state and zip. To view actual availability statistics for a specific area, please use the lead count checker above.
            </p>
            <p className="text-sm text-red-900">
              <strong>⚠️ DNC Compliance:</strong> You are 100% responsible for verifying phone numbers against the National Do Not Call Registry before calling. 
              Owner Connect provides DNC status as a courtesy only and makes no guarantees about accuracy.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [county, setCounty] = useState('');
  const [counties, setCounties] = useState([]);
  const [loadingCounties, setLoadingCounties] = useState(false);
  const [searchType, setSearchType] = useState('zip'); // 'zip' or 'county'
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  // Prefetch signup and login pages for faster navigation
  const signupPrefetch = usePrefetch(() => import('@/pages/SignupPage'));
  const loginPrefetch = usePrefetch(() => import('@/pages/LoginPage'));
  
  // Prefetch auth pages during idle time
  usePrefetchOnIdle([
    () => import('@/pages/SignupPage'),
    () => import('@/pages/LoginPage'),
  ], 3000);

  // Fetch counties when state changes
  const fetchCounties = async (selectedState) => {
    if (!selectedState) {
      setCounties([]);
      return;
    }

    setLoadingCounties(true);
    try {
      const response = await axios.get(`${API}/leads/counties/${selectedState}`);
      setCounties(response.data.counties || []);
    } catch (error) {
      console.error('Failed to load counties:', error);
      setCounties([]);
    } finally {
      setLoadingCounties(false);
    }
  };

  // Handle state change
  const handleStateChange = (selectedState) => {
    setState(selectedState);
    setZip('');
    setCounty('');
    fetchCounties(selectedState);
  };

  const handleCheck = async () => {
    if (!state) {
      toast.error('Please select a state');
      return;
    }

    if (searchType === 'zip' && zip && zip.length !== 5) {
      toast.error('Please enter a valid 5-digit ZIP code');
      return;
    }

    if (searchType === 'county' && !county) {
      toast.error('Please select a county');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        state,
        zip: searchType === 'zip' && zip ? zip : null,
        county: searchType === 'county' && county ? county : null
      };
      
      const response = await axios.post(`${API}/leads/check`, payload);
      setResults(response.data);
    } catch (error) {
      toast.error('Failed to check leads');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Cloud className="w-8 h-8 text-blue-600" />
              <span className="text-2xl font-bold text-slate-900">Owner Connect</span>
            </div>
            <div className="flex items-center gap-4">
              <Button
                data-testid="nav-login-btn"
                variant="ghost"
                onClick={() => navigate('/login')}
                {...loginPrefetch}
              >
                Log In
              </Button>
              <Button
                data-testid="nav-signup-btn"
                onClick={() => navigate('/signup')}
                {...signupPrefetch}
              >
                Sign Up
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 py-16 sm:py-24">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
            Build Your Perfect Lead List for
            <span className="text-blue-600"> Home Service Contractors</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto mb-8">
            Owner Connect is a premium lead list platform that helps home service contractors build targeted lead lists with verified homeowner contact data. Whether you need a storm lead list for recent weather events or a general homeowner lead list for your service area, we provide comprehensive data you can trust. Each lead list includes 18+ data points per property—far more than typical lead list providers.
          </p>
          
          {/* Hero CTA Button */}
          <div className="flex justify-center items-center">
            <Button
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 sm:px-8 py-4 sm:py-6 text-base sm:text-lg w-full sm:w-auto max-w-md"
              onClick={() => navigate('/signup')}
              {...signupPrefetch}
            >
              Start Building Your Lead List - Free to Sign Up
            </Button>
          </div>
        </div>

        {/* Lead Checker Widget */}
        <div id="lead-checker" className="max-w-3xl mx-auto mb-16">
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">
              Check Your Lead List Size by Area
            </h2>
            
            {/* State Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Select State
              </label>
              <Select value={state} onValueChange={handleStateChange}>
                <SelectTrigger data-testid="state-select">
                  <SelectValue placeholder="Select State" />
                </SelectTrigger>
                <SelectContent>
                  {US_STATES.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Search Type Toggle */}
            {state && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Search By
                </label>
                <div className="flex gap-4">
                  <button
                    onClick={() => {
                      setSearchType('zip');
                      setCounty('');
                    }}
                    className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                      searchType === 'zip'
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    ZIP Code
                  </button>
                  <button
                    onClick={() => {
                      setSearchType('county');
                      setZip('');
                    }}
                    className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                      searchType === 'county'
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    County
                  </button>
                </div>
              </div>
            )}

            {/* ZIP Code or County Input */}
            {state && (
              <div className="flex flex-col sm:flex-row gap-4 mb-4">
                {searchType === 'zip' ? (
                  <div className="flex-1">
                    <Input
                      data-testid="zip-input"
                      placeholder="Enter ZIP Code (Optional for state-wide)"
                      value={zip}
                      onChange={(e) => setZip(e.target.value.replace(/\D/g, ''))}
                      maxLength={5}
                    />
                  </div>
                ) : (
                  <div className="flex-1">
                    <Select 
                      value={county} 
                      onValueChange={setCounty}
                      disabled={loadingCounties || counties.length === 0}
                    >
                      <SelectTrigger data-testid="county-select">
                        <SelectValue placeholder={
                          loadingCounties 
                            ? "Loading counties..." 
                            : counties.length === 0 
                            ? "No counties available" 
                            : "Select County"
                        } />
                      </SelectTrigger>
                      <SelectContent>
                        {counties.map(c => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <Button
                  data-testid="check-leads-btn"
                  onClick={handleCheck}
                  disabled={loading || !state}
                  className="px-8"
                >
                  {loading ? 'Checking...' : 'Check Leads'}
                </Button>
              </div>
            )}

            {results && (
              <div className="mt-6 p-6 bg-blue-50 rounded-lg border border-blue-200">
                <div className="text-center mb-4">
                  <p className="text-sm text-slate-700">
                    Showing results for: <span className="font-semibold">{results.state}</span>
                    {results.county && <span> - {results.county} County</span>}
                    {results.zip && <span> - ZIP {results.zip}</span>}
                    {!results.county && !results.zip && <span> - Entire State</span>}
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded-lg">
                    <div className="text-sm text-slate-600 mb-1">Unique Addresses (with phone)</div>
                    <div className="text-3xl font-bold text-blue-600" data-testid="unique-addresses">
                      {results.uniqueAddresses.toLocaleString()}
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg">
                    <div className="text-sm text-slate-600 mb-1">Phone Numbers</div>
                    <div className="text-3xl font-bold text-slate-900" data-testid="total-phones">
                      {results.totalPhones.toLocaleString()}
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg">
                    <div className="text-sm text-slate-600 mb-1">Without Phone Numbers</div>
                    <div className="text-2xl font-bold text-slate-700" data-testid="without-phone">
                      {results.addressesWithoutPhone.toLocaleString()}
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg">
                    <div className="text-sm text-slate-600 mb-1">DNC Status</div>
                    <div className="text-sm">
                      <span className="text-green-600 font-semibold">Can Call: {results.dnc_no.toLocaleString()}</span>
                      <span className="mx-2">|</span>
                      <span className="text-amber-600 font-semibold">DNC: {results.dnc_yes.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Pricing Cards - One-Time Packages */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center text-slate-900 mb-8">Simple Lead List Pricing</h2>
          <p className="text-center text-slate-600 mb-8 max-w-2xl mx-auto">
            Purchase contractor lead lists one-time with no recurring charges. Get instant access to your custom lead list with verified homeowner data.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            <div className="bg-white rounded-xl border border-slate-200 p-8 hover:shadow-lg transition-shadow">
              <div className="text-center mb-6">
                <div className="text-lg font-semibold text-slate-900 mb-2">Starter</div>
                <div className="text-4xl font-bold text-slate-900 mb-1">$1,000</div>
                <div className="text-slate-600">10,000 Leads</div>
                <div className="text-sm text-slate-500 mt-2">10¢ per lead</div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">18+ data points per lead</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">DNC filtering available</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Instant CSV download</span>
                </li>
              </ul>
            </div>

            <div className="bg-blue-600 rounded-xl border-2 border-blue-700 p-8 hover:shadow-lg transition-shadow relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-amber-400 text-slate-900 px-4 py-1 rounded-full text-sm font-semibold">
                Most Popular
              </div>
              <div className="text-center mb-6">
                <div className="text-lg font-semibold text-white mb-2">Professional</div>
                <div className="text-4xl font-bold text-white mb-1">$2,500</div>
                <div className="text-blue-100">30,000 Leads</div>
                <div className="text-sm text-blue-200 mt-2">8.3¢ per lead</div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-blue-200 flex-shrink-0 mt-0.5" />
                  <span className="text-white">18+ data points per lead</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-blue-200 flex-shrink-0 mt-0.5" />
                  <span className="text-white">DNC filtering available</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-blue-200 flex-shrink-0 mt-0.5" />
                  <span className="text-white">Instant CSV download</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-blue-200 flex-shrink-0 mt-0.5" />
                  <span className="text-white">Priority support</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-8 hover:shadow-lg transition-shadow">
              <div className="text-center mb-6">
                <div className="text-lg font-semibold text-slate-900 mb-2">Enterprise</div>
                <div className="text-4xl font-bold text-slate-900 mb-1">$0.05</div>
                <div className="text-slate-600">Per Lead</div>
                <div className="text-sm text-slate-500 mt-2">100,000+ leads</div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Unlimited leads</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">No overage charges</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">18+ data points per lead</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Dedicated support</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Monthly Subscriptions */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center text-slate-900 mb-8">Monthly Subscriptions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {/* Starter */}
            <div className="bg-white rounded-xl border-2 border-blue-200 p-8 hover:shadow-lg transition-shadow">
              <div className="text-center mb-6">
                <div className="text-xl font-bold text-slate-900 mb-2">Starter</div>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-bold text-slate-900">$500</span>
                  <span className="text-slate-600">/month</span>
                </div>
                <div className="text-slate-600 mt-2">5,000 addresses/month</div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Storm alerts</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Property data</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">CSV exports</span>
                </li>
              </ul>
            </div>

            {/* Standard (Popular) */}
            <div className="bg-blue-600 rounded-xl border-2 border-blue-700 p-8 hover:shadow-lg transition-shadow relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-cyan-400 text-slate-900 px-4 py-1 rounded-full text-sm font-bold uppercase">
                Popular
              </div>
              <div className="text-center mb-6">
                <div className="text-xl font-bold text-white mb-2">Standard</div>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-bold text-white">$1,500</span>
                  <span className="text-blue-100">/month</span>
                </div>
                <div className="text-blue-100 mt-2">20,000 addresses/month</div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-blue-200 flex-shrink-0 mt-0.5" />
                  <span className="text-white">Everything in Starter</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-blue-200 flex-shrink-0 mt-0.5" />
                  <span className="text-white">CRM integrations</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-blue-200 flex-shrink-0 mt-0.5" />
                  <span className="text-white">Custom alerts</span>
                </li>
              </ul>
            </div>

            {/* Pro */}
            <div className="bg-slate-800 rounded-xl border-2 border-slate-700 p-8 hover:shadow-lg transition-shadow">
              <div className="text-center mb-6">
                <div className="text-xl font-bold text-white mb-2">Pro</div>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-bold text-white">$5,000</span>
                  <span className="text-slate-300">/month</span>
                </div>
                <div className="text-slate-300 mt-2">100,000 addresses/month</div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span className="text-white">Everything in Standard</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span className="text-white">Priority support</span>
                </li>
              </ul>
            </div>
          </div>
          
          {/* Additional Address Pricing Note */}
          <div className="text-center mt-6">
            <p className="text-slate-600">
              Monthly subscriptions include <span className="font-semibold text-slate-900">$0.08 per additional address</span>
            </p>
          </div>
        </div>

        {/* Preview Sample Lead Data Section */}
        <div className="mb-16">
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 sm:p-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-center text-slate-900 mb-4">Preview Sample Lead List Data</h2>
            <p className="text-center text-slate-600 mb-8">See actual homeowner data from your lead list by ZIP code (up to 10 records)</p>
            
            <SampleLeadPreview />
            
            {/* CTA after Sample Data */}
            <div className="text-center mt-8">
              <Button
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 sm:px-8 py-4 sm:py-6 text-base sm:text-lg font-semibold w-full sm:w-auto max-w-md mx-auto"
                onClick={() => navigate('/signup')}
              >
                Get Started Today - Free to Sign Up
              </Button>
            </div>
          </div>
        </div>

        {/* More Data Than Competition Section */}
        <div className="mb-16">
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-12 text-white">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-3">
              The Most Comprehensive Lead Lists in the Industry
            </h2>
            <p className="text-center text-blue-100 text-lg mb-12">
              18+ data points per property in every lead list—not just basic contact info
            </p>
            <p className="text-center text-blue-50 mb-12 max-w-3xl mx-auto">
              Most contractor lead list providers only give you names and addresses. Owner Connect delivers comprehensive homeowner lead lists with everything you need to qualify prospects and close deals faster.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {/* Contact Information */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="flex items-center gap-2 mb-4">
                  <Users className="w-6 h-6 text-blue-200" />
                  <h3 className="text-xl font-bold">Contact Information</h3>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span>First & Last Name</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span>Phone Number</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span>Complete Address</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span>Age</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span>DNC Status</span>
                  </li>
                </ul>
              </div>

              {/* Financial Qualifying Data */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="flex items-center gap-2 mb-4">
                  <DollarSign className="w-6 h-6 text-green-400" />
                  <h3 className="text-xl font-bold">Financial Qualifying Data</h3>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span>Credit Score Range</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span>Income Level</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span>Home Value</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span>Owner vs Renter</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span>Home Age / Year Built</span>
                  </li>
                </ul>
              </div>

              {/* Storm Damage History */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="flex items-center gap-2 mb-4">
                  <Cloud className="w-6 h-6 text-blue-300" />
                  <h3 className="text-xl font-bold">Storm Damage History</h3>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span>Max Wind Speed (36 mo.)</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span>Wind Event Date</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span>Max Hail Size (36 mo.)</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span>Hail Event Date</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Comparison Footer */}
            <div className="text-center">
              <p className="text-lg text-blue-100 mb-3">
                Compare this to typical lead list providers who only give you:
              </p>
              <div className="inline-block bg-red-900/50 border-2 border-red-500/50 rounded-lg px-8 py-4">
                <p className="text-xl font-semibold text-red-200">
                  ❌ Name, Address, Phone... and that's it.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* SEO Content Section - Moved Above CTA */}
        <div className="max-w-4xl mx-auto px-4 py-12 mb-8">
          <div className="bg-slate-50 rounded-xl p-8 border border-slate-200">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Why Owner Connect Is the Best Lead List Provider</h2>
            <div className="prose prose-slate max-w-none">
              <p className="text-slate-700 mb-4">
                Owner Connect specializes in providing premium lead lists for home service contractors. Our lead list platform gives you access to millions of verified homeowner records with comprehensive data points that help you qualify prospects before you call. Whether you're a roofer, HVAC contractor, plumber, or electrician, our lead lists are designed specifically for your business needs.
              </p>
              <p className="text-slate-700 mb-4">
                Unlike basic lead list services that only provide names and phone numbers, every Owner Connect lead list includes property information, financial qualifying data, storm damage history, and DNC status. This allows you to build highly targeted lead lists based on your specific criteria—from small, local lead lists to large national lead lists covering multiple states.
              </p>
              <p className="text-slate-700">
                Whether you need a small homeowner lead list for a local campaign or a large-scale contractor lead list for multi-state operations, Owner Connect offers flexible pricing options. Choose from one-time lead list purchases or monthly subscriptions. Start building your custom lead list today and see why thousands of home service contractors trust Owner Connect for their homeowner lead lists.
              </p>
            </div>
          </div>
        </div>

        {/* Bottom CTA Section */}
        <div className="text-center mb-16">
          <div className="bg-blue-600 rounded-2xl p-12 text-white">
            <h2 className="text-3xl font-bold mb-4">Ready to Build Your Lead List?</h2>
            <p className="text-lg text-blue-100 mb-6 max-w-2xl mx-auto">
              Join hundreds of home service professionals who trust Owner Connect as their lead list provider for high-quality homeowner lead lists with verified data.
            </p>
            <Button
              data-testid="cta-signup-btn"
              size="lg"
              className="bg-white text-blue-600 hover:bg-blue-50 px-4 sm:px-8 py-4 sm:py-6 text-base sm:text-lg w-full sm:w-auto max-w-md mx-auto"
              onClick={() => navigate('/signup')}
            >
              Start Building Your Lead List - Free to Sign Up
            </Button>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-4xl mx-auto px-4 mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-slate-900 mb-4">Frequently Asked Questions About Lead Lists</h2>
          <p className="text-center text-slate-600 mb-8">Everything you need to know about building and using lead lists</p>
          
          <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6 sm:p-8">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="faq-1">
                <AccordionTrigger className="text-left text-slate-900 font-semibold">
                  What is a lead list?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  A lead list is a collection of potential customer contact records that match your target market. Owner Connect provides homeowner lead lists containing verified names, phone numbers, addresses, demographic information, and storm-impact history so contractors can begin outreach immediately.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="faq-2">
                <AccordionTrigger className="text-left text-slate-900 font-semibold">
                  How does Owner Connect build its lead lists?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  Owner Connect compiles lead lists from verified homeowner data sources including property records, behavioral data, and demographic information. Each lead list includes 18+ data points so contractors can qualify prospects before calling.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="faq-3">
                <AccordionTrigger className="text-left text-slate-900 font-semibold">
                  Who should use a lead list from Owner Connect?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  Our lead lists are designed for roofers, HVAC companies, electricians, plumbers, remodelers, and any home service business that relies on outbound calling, canvassing, or direct outreach. Businesses targeting homeowners benefit most from our detailed lead list data.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="faq-4">
                <AccordionTrigger className="text-left text-slate-900 font-semibold">
                  Can I create a highly targeted lead list for one ZIP code or neighborhood?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  Yes. You can build a lead list for a single ZIP code, multiple ZIP codes, or an entire state. Owner Connect lets you filter by geography and preview sample lead list records before purchasing.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="faq-5">
                <AccordionTrigger className="text-left text-slate-900 font-semibold">
                  What data points are included in each lead list?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  Every lead list includes homeowner name, phone number, address, DNC status, property information, financial indicators, home age, and storm history data such as max hail size and wind speed over the last 36 months. This helps contractors qualify prospects more effectively.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="faq-6">
                <AccordionTrigger className="text-left text-slate-900 font-semibold">
                  What is the difference between a storm lead list and a general lead list?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  A storm lead list contains properties impacted by recent hail or wind events, while a general lead list includes homeowner data for any area you choose. Both lists include the same comprehensive property and demographic data.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="faq-7">
                <AccordionTrigger className="text-left text-slate-900 font-semibold">
                  Can I export my lead list into my CRM or dialer?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  Yes. Every lead list is available as an instant CSV download so you can import it into any CRM, dialer, or spreadsheet software. No limitations or proprietary formats.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="faq-8">
                <AccordionTrigger className="text-left text-slate-900 font-semibold">
                  How much does a lead list cost?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  Owner Connect offers both one-time lead list purchases and monthly subscriptions. Pricing depends on the number of leads you need, but every plan includes 18+ data points per record and instant CSV access.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="faq-9">
                <AccordionTrigger className="text-left text-slate-900 font-semibold">
                  Is DNC compliance included in my lead list?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  Owner Connect provides DNC status as a courtesy within each lead list, but you are responsible for verifying phone numbers against the National Do Not Call Registry before calling. We do not guarantee DNC accuracy.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="faq-10">
                <AccordionTrigger className="text-left text-slate-900 font-semibold">
                  Do lead lists expire after I purchase them?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  No. One-time lead list purchases never expire. You receive permanent access to any lead list you download.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="faq-11">
                <AccordionTrigger className="text-left text-slate-900 font-semibold">
                  Can Owner Connect generate lead lists for commercial properties?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  Yes. Owner Connect supports both residential and commercial contact data depending on the area. You can build a homeowner lead list, commercial lead list, or a combination of both.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="faq-12">
                <AccordionTrigger className="text-left text-slate-900 font-semibold">
                  How do I get started building my lead list?
                </AccordionTrigger>
                <AccordionContent className="text-slate-700">
                  Just create a free account, choose your state or ZIP code, check how many lead list records are available, and preview sample data. No credit card is required to start building your lead list.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center gap-2 mb-4 md:mb-0">
              <Cloud className="w-6 h-6" />
              <span className="text-lg font-semibold">Owner Connect</span>
            </div>
            <div className="flex gap-6 text-sm text-slate-400">
              <button onClick={() => navigate('/terms')} className="hover:text-white transition-colors">
                Terms & Conditions
              </button>
              <button onClick={() => navigate('/privacy')} className="hover:text-white transition-colors">
                Privacy Policy
              </button>
            </div>
          </div>
          <div className="mt-8 text-center text-sm text-slate-400">
            © 2025 Owner Connect. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
